import { useIsMobileWithSsrFallback } from 'hooks/useIsMobileWithSsrFallback'
import clsx from 'clsx'
import { replace } from 'lib/routing/appRouting'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { isRegionFilterBypassedForOffer } from 'selectors/marketingSelectors'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import Pane from 'components/Common/Pane'
import OfferPageGallery from '../OfferPageGallery'

import { fetchRelatedTravelItems } from 'actions/OfferActions'
import { CLEAR_OFFER_CHECK_IN_CHECK_OUT, SET_OFFER_CHECK_IN_CHECK_OUT } from 'actions/actionConstants'

import Breakpoint from 'components/Common/Breakpoint'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import DesktopRelatedTravelItems from 'components/OfferPage/RelatedTravelItems'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import { urlHashDatePicker } from 'constants/url'
import useLereDynamicPricingGroupForRates from 'hooks/useLereDynamicPricingGroupForRates'
import type { FlashSameRoomSelection, OfferPageFilterType, OfferPageMultiRoomMode, OfferPagePriceMode, OfferPageRoom, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useMountedEffect from 'hooks/useMountedEffect'
import usePrevious from 'hooks/usePrevious'
import useRebrandVersion from 'hooks/useRebrandVersion'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { hasOfferExpired } from 'lib/offer/offerUtils'

import type { OfferDurationOption } from 'lib/offer/offerDurationTypes'
import OfferIntro from './FlashHotelIntro'
import FlashHotelSidebarFilters from './FlashHotelSidebarFilters'
import FlashHotelHeader from './FlashHotelStickyHeader'
import HotelOfferPageStickyFooter from '../HotelOfferPageStickyFooter'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import LuxPlusNotificationBanner from 'luxPlus/components/Banners/LuxPlusNotificationBanner'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import AccommodationOfferPageStickyFooter from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyFooter'
import { encodeSearchParams, isRoomsInvalid } from 'lib/search/searchUtils'
import useShareModalConfig from 'hooks/useShareModalConfig'
import WalledContent from 'components/Common/WalledContent'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import AccommodationOfferPageStickyHeaderMobile from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyHeaderMobile'
import WishlistGuard from 'wishlists/components/WishlistGuard'
import PendingLoginModalContext from 'contexts/PendingLoginModalContext'
import useFlashOfferSignUpModal from './useFlashOfferSignUpModal'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import {
  getCalendarDurationOptionsWithCharterBundleRestrictions,
  getCanUseMultiRoomUI,
  getCheapestSelectedPackageOptionWithDefault,
  getCheckoutDate,
  getEffectiveCharterBundleRoute,
  getEffectiveBundledWithFlightsOnly,
  isAvailableForPurchase,
  offerFlightsEnabled,
} from 'selectors/offerPage/offerPageSelectors'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import { useLocation } from 'react-router-dom'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useOfferPageAirportEffects from 'hooks/OfferPage/useOfferPageAirportEffects'
import FlashMobileStickyHeaderFilters from './FlashHotelMobileVariant/FlashMobileStickyHeaderFilters'
import { hasSelectedRooms, hasSelectedRoomsOrDefault } from 'lib/offer/offerPageUtil'
import { trackDurationMs } from 'analytics/perf'
import useHotelIndividualRoomAutoselection from 'hooks/OfferPage/useHotelIndividualRoomAutoselection'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import FlashHotelMobileRoomOptions from './FlashHotelMobileVariant/FlashHotelMobileRoomOptions'
import useHasMounted from 'hooks/useHasMounted'
import useCharterFlightLabels from 'hooks/Flights/useCharterFlightLabels'
import { toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { useCharterBundleFallbackPricing } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useCharterBundleFallbackPricing'
import useCharterBundleFlightPreview from 'hooks/CharterFlights/useCharterBundleFlightPreview'
import { filterDateRangesByNights } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/utils'
import FlashHotelDesktopRoomOptions from './FlashHotelDesktopRoomOptions'
import { useIntl } from 'react-intl'
import useFlashHotelAvailableRates from 'hooks/OfferPage/useFlashHotelAvailableRates'
import useFlashHotelCalendarRequests from 'hooks/OfferPage/useFlashHotelCalendarRequests'
import SocialProofDialog from './SocialProofDialog'
import FlashOfferSignUpWall from './FlashOfferSignUpWall'
import { shouldAutoScrollToFilters } from './flashHotelFilterScrollUtils'
import useExperiment from 'hooks/Experiments/useExperiment'
import { ChertoExperiments } from 'constants/experiments'
import { useFixedFlashLengthOfStayExperimentState } from 'components/OfferPage/Filters/CalendarPicker/useFixedFlashLengthOfStayExperiment'
import { useFlashOfferLosCalendarExperimentState } from 'components/OfferPage/Filters/CalendarPicker/useFlashOfferLosCalendarExperiment'
import { isFlashOfferLosCalendarEligible } from 'components/OfferPage/Filters/CalendarPicker/flashOfferLosCalendarEligibility'
import { getFixedFlashCheckoutDate } from 'components/OfferPage/Filters/CalendarPicker/fixedFlashLengthOfStayCalendarUtils'
import { useInView } from 'react-intersection-observer'
import useFlashPackageSelectionExperiment from './packageSelection/useFlashPackageSelectionExperiment'
import { isFlashPackageSelectionEligible } from './packageSelection/flashPackageSelectionEligibility'
import { getFlashSameRoomUrlSelection } from './packageSelection/flashSameRoomSelection'
import { useWeddingGiftRegistryContext } from 'weddings/contexts/WeddingGiftRegistryContext'

const RelativeBlock = styled.div`
  position: relative;
`

const StyledFlashHotelStickyHeader = styled(FlashHotelHeader)`
  position: relative;

  &.hideOnMobile.hideOnMobile {
    display: none;

    ${mediaQueryUp.tablet} {
      display: flex;
      visibility: visible;
    }
  }
`

const PackageOptionsPane = styled(Pane)`
  ${mediaQueryDown.mobile} {
    &.isCharterBundle {
      padding-top: 0;
    }
  }
`

const PackageOptionsContent = styled.div`
  padding: ${rem(20)} 0;
`

const FilterContainer = styled.div`
  padding: ${rem(16)} ${rem(16)} 0;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

interface Props {
  offer: App.HotelOffer
  airport?: App.Airport
  airportSource: OfferPageState['airportSource']
  duration: number
  activeFilter?: OfferPageFilterType
  activeHeaderFilter?: OfferPageFilterType
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  checkOutDate?: string
  selectedCheckOutDate?: string
  availableForPurchase?: boolean
  bestPackage: App.OfferPackageOptionView
  room?: App.PackageRoomType
  flightsEnabled?: boolean
  isFlightsSelected: boolean
  roomMode: OfferPageMultiRoomMode
  sameRoomSelection?: FlashSameRoomSelection
  flashPackageSelectionActive?: boolean
  multipleRoomBookingActive: boolean
  canUseMultiRoomUI: boolean
  charterBundleRoute?: App.CharterBundleRoute
  charterBundleEnabled: boolean
  fareClass: FareClassWithReward
  priceMode: OfferPagePriceMode
  isPriceModeChangedFromDefault: boolean
  effectiveBundledWithFlightsOnly: boolean
  effectiveCharterBundleRoute?: App.CharterBundleRoute
  calendarDurationOptions: Array<OfferDurationOption>
}

/**
 * Until there is a formal way to set a video to auto play on the offer itself
 * We'll see a magic list of offer ids for now
 */
const magicAutoPlayVideoOfferIds = new Set(['006RF00000CoK6HYAV', '006RF00000DX4JtYAL'])

const OFFER_GALLERY_TOP_ROOT_ID = 'offer-gallery-top-root'

function FlashHotelContent(props: Props) {
  const {
    offer,
    airport,
    airportSource,
    duration,
    activeFilter,
    rooms,
    checkInDate,
    checkOutDate,
    selectedCheckOutDate,
    availableForPurchase,
    bestPackage,
    room,
    activeHeaderFilter,
    flightsEnabled,
    isFlightsSelected,
    roomMode,
    sameRoomSelection,
    flashPackageSelectionActive,
    multipleRoomBookingActive,
    canUseMultiRoomUI,
    charterBundleRoute,
    charterBundleEnabled,
    fareClass,
    priceMode,
    isPriceModeChangedFromDefault,
    effectiveBundledWithFlightsOnly,
    effectiveCharterBundleRoute,
    calendarDurationOptions,
  } = props

  const intl = useIntl()
  const showFlashOfferSignUpModal = useFlashOfferSignUpModal()
  const showWishlistLoginModal = useCallback(() => showFlashOfferSignUpModal(
    offer,
    bestPackage,
    { initialMode: 'wishlistLogin' },
    checkInDate,
  ), [bestPackage, checkInDate, offer, showFlashOfferSignUpModal])
  const pendingLoginModalHandlers = useMemo(() => ({
    wishlistLogin: showWishlistLoginModal,
  }), [showWishlistLoginModal])
  const isRebrand = useRebrandVersion() === 'rebrand'
  const { charterBundleDurationOptions } = useCharterFlightLabels()
  const location = useLocation()
  const routerLocation = useAppSelector(state => state.router.location)
  const { originAirports, airports } = useContext(GeoContext)
  const luxPlusEnabled = useAppSelector(state => getIsLuxPlusEnabled(state))
  const headlessMode = useAppSelector(state => state.system.headlessMode)
  const prevPath = useAppSelector(state => state.routeHistory.prevPath)
  const bypassRegionFilter = useAppSelector(state => isRegionFilterBypassedForOffer(state, offer.id))
  const isMobileDevice = useScreenSizeOnly('mobile')
  const { isActive: isWeddingGiftRegistry } = useWeddingGiftRegistryContext()
  const packageSelectionEligible = isFlashPackageSelectionEligible({
    availableForPurchase: !!availableForPurchase,
    isFlightsSelected,
    mustBundleWithFlights: effectiveBundledWithFlightsOnly,
    hasCharterBundle: !!charterBundleRoute,
    isWeddingGiftRegistry,
    walledGarden: offer.walledGarden,
  })
  const { ref: packageSelectionRef, inView: packageSelectionInView } = useInView({ threshold: 0, triggerOnce: true })
  const packageSelectionExperiment = useFlashPackageSelectionExperiment({
    isEligible: packageSelectionEligible,
    isIndividualMode: multipleRoomBookingActive,
    isInView: packageSelectionInView,
    isReady: !!bestPackage,
  })
  const showPackageSelection = packageSelectionExperiment.isOptionD && !multipleRoomBookingActive

  // calendars only start fetching once the user has tried to look at dates
  // after that, try keep the calendars up to date
  const [viewedDates, setViewedDates] = useState(!!checkInDate)
  const [calendarOpenedOfferId, setCalendarOpenedOfferId] = useState<string | undefined>(undefined)
  const isCalendarOpen = activeFilter === 'checkin' || activeHeaderFilter === 'checkin'
  const hasDatesSelected = !!checkInDate
  const hasOpenedCalendar = calendarOpenedOfferId === offer.id
  const calendarEntryPointActivated = hasOpenedCalendar || isCalendarOpen
  const shouldRequestLereDecision = hasDatesSelected || calendarEntryPointActivated

  useEffect(() => {
    if (hasDatesSelected && !viewedDates) {
      setViewedDates(true)
    }
  }, [hasDatesSelected, viewedDates])

  useEffect(() => {
    if (isCalendarOpen && !hasOpenedCalendar) {
      setCalendarOpenedOfferId(offer.id)
    }
  }, [hasOpenedCalendar, isCalendarOpen, offer.id])

  const { pricing, pricingWithFlights } = bestPackage
  const isHotelOnlyMode = !!charterBundleRoute && !charterBundleEnabled
  const bundledWithFlightsOnly = effectiveBundledWithFlightsOnly
  const shouldShowDiscountPercent = pricing.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = pricingWithFlights && pricingWithFlights.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const showDiscountPill = !offer?.isDiscountPillHidden && (
    (shouldShowDiscountPercent && !bundledWithFlightsOnly) ||
    (bundledWithFlightsOnly && shouldShowBundledDiscountPercent)
  )
  const loggedOutSavedPercent = showDiscountPill
    ? bundledWithFlightsOnly
      ? pricingWithFlights?.discountPercent
      : Math.max(pricing.discountPercent, pricingWithFlights?.discountPercent ?? 0)
    : undefined

  const dispatch = useAppDispatch()
  const stateDispatch = useOfferPageDispatch()
  useEffect(() => {
    const active = packageSelectionExperiment.isOptionD
    if (!!flashPackageSelectionActive !== active) stateDispatch({ type: OfferPageActions.setFlashPackageSelectionActive, active })
  }, [flashPackageSelectionActive, packageSelectionExperiment.isOptionD, stateDispatch])
  const packageOptionsAnchor = useRef<HTMLDivElement>(null)
  const topImageLoadSentRef = useRef<boolean>(false)
  const topImageStartMsRef = useRef<number>(0)
  const scrollAfterFilterClose = useRef<boolean>(false)
  const previousRooms = usePrevious(rooms)
  const previousCheckIn = usePrevious(checkInDate)
  const previousFilter = usePrevious(activeFilter)
  const desktopFilterBlockRef = useRef<HTMLDivElement>(null)
  const [stickyFooterVisible, setIsStickyFooterVisible] = useState<boolean>(true)
  const fareClassForSearch = toBaseFareClass(fareClass)

  const { dateRangesByMonth: charterDateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute,
    occupancies: rooms,
    fareClass: fareClassForSearch,
    disabled: !charterBundleRoute || rooms.length === 0,
    feederOriginAirportCode: airport?.code,
  })

  const dateRangesByMonth = useMemo(
    () => filterDateRangesByNights(charterDateRangesByMonth, duration),
    [charterDateRangesByMonth, duration],
  )

  const { totalFlightPrice: flightPrice } = useOfferPageFlightPrice()

  const { cheapestDateRange } = useCharterBundleFallbackPricing({
    offer,
    rooms,
    charterBundleRoute: effectiveCharterBundleRoute,
    fareClass: fareClassForSearch,
    disabled: !effectiveCharterBundleRoute || !!checkInDate,
    feederOriginAirportCode: airport?.code,
  })

  const crossSellFlightPreview = useCharterBundleFlightPreview({
    offer, rooms, fareClass: fareClassForSearch, duration, checkIn: checkInDate, charterBundleRoute: effectiveCharterBundleRoute,
  })

  const charterBundleCheckoutRange = useMemo(() => {
    if (!charterBundleRoute) return undefined
    const allRanges = Object.values(charterDateRangesByMonth).flatMap((ranges) => ranges)
    if (allRanges.length === 0) return undefined

    const minCheckOut = allRanges.reduce<string>((minValue, range) => {
      if (!minValue) return range.checkOut
      return moment(range.checkOut).isBefore(minValue) ? range.checkOut : minValue
    }, '')
    const maxCheckOut = allRanges.reduce<string>((maxValue, range) => {
      if (!maxValue) return range.checkOut
      return moment(range.checkOut).isAfter(maxValue) ? range.checkOut : maxValue
    }, '')

    return minCheckOut && maxCheckOut ? { minCheckOut, maxCheckOut } : undefined
  }, [charterBundleRoute, charterDateRangesByMonth])

  useEffect(() => {
    if (!charterBundleRoute || !charterBundleCheckoutRange) return
    stateDispatch({
      type: OfferPageActions.setCharterBundleCheckoutRange,
      charterBundleCheckoutRange,
    })
  }, [charterBundleRoute, charterBundleCheckoutRange, stateDispatch])

  const filteredDurationOptions = useMemo(() => {
    return charterBundleDurationOptions.filter(option => {
      return offer.packages.some(pkg => pkg.duration === option.value)
    })
  }, [offer.packages, charterBundleDurationOptions])

  useEffect(() => {
    if (!effectiveCharterBundleRoute) return
    if (filteredDurationOptions.length === 0) return

    const isValidCharterDuration = duration === 5 || duration === 12
    const hasMatchingPackage = filteredDurationOptions.some(opt => opt.value === duration)
    const isValidDuration = isValidCharterDuration && hasMatchingPackage

    if (!isValidDuration) {
      const defaultDuration = filteredDurationOptions[0].value
      if (checkInDate) {
        const newCheckOut = moment(checkInDate).add(defaultDuration, 'days').format(ISO_DATE_FORMAT)
        stateDispatch({ type: OfferPageActions.setDates, checkIn: checkInDate, checkOut: newCheckOut })
      } else {
        stateDispatch({ type: OfferPageActions.setDuration, duration: defaultDuration })
      }
    }
  }, [effectiveCharterBundleRoute, duration, checkInDate, stateDispatch, filteredDurationOptions])

  // Keep duration valid for hotel-only mode (5/6/7 nights only).
  useEffect(() => {
    if (!isHotelOnlyMode) return
    if (calendarDurationOptions.length === 0) return

    const hasValidDuration = calendarDurationOptions.some(option => option.duration === duration)
    if (!hasValidDuration) {
      stateDispatch({ type: OfferPageActions.setDuration, duration: calendarDurationOptions[0].duration })
    }
  }, [duration, isHotelOnlyMode, stateDispatch, calendarDurationOptions])

  const handleCharterDurationChange = useCallback((newNights: number) => {
    // Use setDuration which properly updates duration state and recalculates checkOut if needed
    stateDispatch({ type: OfferPageActions.setDuration, duration: newNights })
  }, [stateDispatch])

  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden(offer.type)
  useIdleTimeReporter(IdleTimeSource.FlashHotel)
  const {
    // Read bucket without Snowplow activation so checkout derivation and calendar fetch can
    // react before the calendar UI opens. Calendar pickers activate on entry (timing differs
    // by platform); variation-change refetch below handles late client-side rebucketing.
    isEnabled: isFixedFlashLengthOfStayVariant,
    // Tracked for calendar re-fetch when the client-side bucket changes after the picker opens.
    variation: fixedFlashLengthOfStayVariation,
  } = useFixedFlashLengthOfStayExperimentState()
  const {
    isVariant: isFlashOfferLosCalendarVariant,
    variation: flashOfferLosCalendarVariation,
  } = useFlashOfferLosCalendarExperimentState()
  const isFlashOfferLosCalendarEligibleOffer = isFlashOfferLosCalendarEligible(offer, charterBundleRoute)
  const isFlashOfferLosCalendarEnabled = isFlashOfferLosCalendarVariant && isFlashOfferLosCalendarEligibleOffer
  const trackedLosVariation = isFlashOfferLosCalendarEligibleOffer ? flashOfferLosCalendarVariation : undefined
  const previousTrackedLosVariation = usePrevious(trackedLosVariation)
  // The LOS selector owns duration selection. Do not also run the older fixed-length date flow.
  const isFixedFlashLengthOfStayEnabled = isFixedFlashLengthOfStayVariant && !isFlashOfferLosCalendarEnabled
  const previousFixedFlashLengthOfStayVariation = usePrevious(fixedFlashLengthOfStayVariation)
  const effectiveCheckOutDate = getFixedFlashCheckoutDate({
    isFixedFlashLengthOfStayEnabled,
    selectedCheckOut: selectedCheckOutDate,
    fallbackCheckOut: checkOutDate,
  })
  const { lereGroup, isReady: isLereGroupReady } = useLereDynamicPricingGroupForRates({
    shouldWait: shouldRequestLereDecision,
    hasOpenedCalendar: calendarEntryPointActivated,
    offerId: offer.id,
    checkIn: checkInDate,
    checkOut: effectiveCheckOutDate,
  })

  const queryString = useMemo(() => {
    const selectedPackage = getFlashSameRoomUrlSelection(offer, rooms, sameRoomSelection, showPackageSelection)
    return encodeSearchParams({
      urlSearch: location.search,
      dates: {
        checkIn: checkInDate,
        checkOut: effectiveCheckOutDate,
      },
      rooms,
      multiRoomMode: roomMode,
      sameRoomSelection: selectedPackage,
      fareClass,
      priceMode,
      isPriceModeChangedFromDefault,
      flights: isFlightsSelected,
    }).toString()
  }, [showPackageSelection, checkInDate, effectiveCheckOutDate, rooms, roomMode, sameRoomSelection, offer, fareClass, location.search, priceMode, isPriceModeChangedFromDefault, isFlightsSelected])

  // Need to pass the context state above the tree
  useEffect(() => {
    dispatch({
      type: SET_OFFER_CHECK_IN_CHECK_OUT,
      payload: {
        offerId: offer.id,
        checkIn: checkInDate,
        checkOut: effectiveCheckOutDate,
        duration,
        rooms,
      },
    })

    return
  }, [dispatch, offer.id, checkInDate, duration, rooms, effectiveCheckOutDate])

  useEffect(() => {
    // clear the check in/check out on unmount
    return () => {
      dispatch({
        type: CLEAR_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: offer.id,
        },
      })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch(fetchRelatedTravelItems(offer, checkInDate, effectiveCheckOutDate, rooms))
  }, [dispatch, offer, checkInDate, rooms, effectiveCheckOutDate])

  useMountedEffect(() => {
    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
      previousRooms !== rooms ||
      previousCheckIn !== checkInDate
  }, [rooms, checkInDate])

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const scrollToPackageOptions = useCallback(() => {
    requestAnimationFrame(() => {
      packageOptionsAnchor.current?.scrollIntoView({ behavior: 'auto' })
    })
  }, [])

  useEffect(() => {
    topImageStartMsRef.current = performance.now()
    topImageLoadSentRef.current = false
    return
  }, [offer.id])

  // 'auto' scroll (not from a user action) only occurs on tablet and up sizes
  // as we don't want the users scroll to lose their position on mobile
  const autoScrollToFilters = useCallback(() => {
    if (isTabletScreenSizeAndUp) {
      requestAnimationFrame(() => {
        desktopFilterBlockRef.current?.scrollIntoView({ behavior: 'auto' })
      })
    }
  }, [isTabletScreenSizeAndUp])

  const selectedDurationPackages = useMemo(() => {
    return offer.packages.filter(p => p.duration === duration)
  }, [duration, offer.packages])

  const occupancyCalendarPackages = selectedDurationPackages
  const combinedCalendarPackages = isFixedFlashLengthOfStayEnabled ? offer.packages : selectedDurationPackages

  const { requestCalendars: fetchCalendarData, hasSearchChanged } = useFlashHotelCalendarRequests({
    offer,
    rooms,
    flightOrigin: airport?.code,
    enabled: isLereGroupReady,
    fetchCombined: isMobileDevice || !multipleRoomBookingActive,
    packages: occupancyCalendarPackages,
    combinedPackages: combinedCalendarPackages,
    bypassRegionFilter,
    lereGroup,
  })

  useEffect(() => {
    if (!viewedDates || !isLereGroupReady) {
      return
    }

    fetchCalendarData()
  }, [viewedDates, isLereGroupReady, fetchCalendarData])

  // The calendar may already be open when the fixed-flash experiment bucket arrives or changes
  // on the client (Optimizely preview, post-login rebucket, late decision). The initial fetch
  // uses whatever combinedCalendarPackages was at open time, so a later variant switch can leave
  // checkout options stuck on a single duration. Re-fetch once variation changes after open.
  useEffect(() => {
    if (!viewedDates) return
    const fixedFlashUnchanged = fixedFlashLengthOfStayVariation === previousFixedFlashLengthOfStayVariation
    const losUnchanged = trackedLosVariation === previousTrackedLosVariation
    if (fixedFlashUnchanged && losUnchanged) return
    const fixedFlashPreBucket = previousFixedFlashLengthOfStayVariation === undefined && fixedFlashLengthOfStayVariation === undefined
    const losPreBucket = previousTrackedLosVariation === undefined && trackedLosVariation === undefined
    if (fixedFlashPreBucket && losPreBucket) return

    fetchCalendarData()
  }, [
    viewedDates,
    fixedFlashLengthOfStayVariation,
    previousFixedFlashLengthOfStayVariation,
    trackedLosVariation,
    previousTrackedLosVariation,
    fetchCalendarData,
  ])

  useFlashHotelAvailableRates({
    offer,
    checkIn: checkInDate,
    checkOut: effectiveCheckOutDate,
    rooms,
    flightOrigin: airport?.code,
    enabled: !activeFilter && isLereGroupReady,
    fetchForEachRoom: showPackageSelection ? multipleRoomBookingActive : canUseMultiRoomUI,
    lereGroup,
  })

  useEffect(() => {
    if (effectiveCharterBundleRoute) {
      fetchCalendarData()
    }
  }, [effectiveCharterBundleRoute, fetchCalendarData])

  useEffect(() => {
    const hasRooms = hasSelectedRooms(rooms)
    const hasDate = !!checkInDate

    if (previousFilter === 'capacity' &&
      !hasRooms &&
      !activeFilter
    ) {
      autoScrollToFilters()
      scrollAfterFilterClose.current = false
    }

    if (shouldAutoScrollToFilters(isFixedFlashLengthOfStayEnabled, previousFilter, activeFilter)) {
      autoScrollToFilters()
    }

    if (previousFilter && !activeFilter && hasRooms && hasDate) {
      if (scrollAfterFilterClose.current) {
        if (room) {
          window.location.hash = room.id
        } else {
          autoScrollToFilters()
        }
      }
      scrollAfterFilterClose.current = false
    }

    if ((activeFilter === 'checkin' || activeHeaderFilter === 'checkin') && !viewedDates) {
      setViewedDates(true)
      fetchCalendarData()
    } else {
      const requiresNewCalendar = (multipleRoomBookingActive || viewedDates) && hasSearchChanged()
      if (requiresNewCalendar) {
        fetchCalendarData()
      }
    }
  }, [activeFilter, activeHeaderFilter, fetchCalendarData, hasSearchChanged]) // oxlint-disable-line react-hooks/exhaustive-deps

  useMountedEffect(() => {
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  useEffect(() => {
    // once we load packages and have dates, get our calendar data
    if (viewedDates && offer.packages) {
      fetchCalendarData()
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [offer.packages, multipleRoomBookingActive])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (viewedDates) {
      fetchCalendarData()
    }

    if (!activeFilter) {
      autoScrollToFilters()
    }
  }, [duration])

  useOfferPageAirportEffects({ airport, airportSource, offerId: offer.id, charterBundleRoute })

  useEffect(() => {
    if (location.hash === urlHashDatePicker && !effectiveCharterBundleRoute) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
  }, []) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(replace({ ...routerLocation, search: queryString }))
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  const occupancyNumber = useMemo(() => sumUpOccupancies(rooms), [rooms])

  const occupancyLimits = useMemo(() => {
    return offer.packages.flatMap((p) => p.roomRate?.capacities ?? [])
  }, [offer])

  const isValidRoomOccupancy = bundledWithFlightsOnly || occupancyNumber < 10
  const destinationAirport = useMemo(() => airports.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])
  const showFlightInformation = !effectiveCharterBundleRoute && !!flightsEnabled && isFlightsSelected && !!destinationAirport && (originAirports.length > 1 || (originAirports.length > 0 && destinationAirport.code !== airport?.code)) && isValidRoomOccupancy && !!flightPrice

  const hideSoldOutOnMobile = !availableForPurchase && config.LERE_EXPIRED_DEALS_V2_ENABLED

  const shareModalConfig = useShareModalConfig({
    image: offer.images[0],
    offerId: offer.id,
    location: [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ') ?? '',
    type: 'offer',
    utmCampaign: 'webshare',
    queryString,
    offerType: 'hotel',
  })

  // Do not show the Expired recommendation when it comes from my escapes
  const shouldShowLere = useMemo(() => hasOfferExpired(offer) && !prevPath.includes('/my-escapes/order'), [offer, prevPath])

  useHotelIndividualRoomAutoselection(!showPackageSelection)

  const hasMounted = useHasMounted()
  const isNewMobileHeaderUIEnabled = useIsMobileWithSsrFallback()

  const invalidRoomSelectionForSticky = !hasSelectedRoomsOrDefault(rooms) || isRoomsInvalid(rooms)
  const canViewOptionsForSticky = !!checkInDate && !invalidRoomSelectionForSticky
  const isNewMobileSingleRoomFlow = !multipleRoomBookingActive
  const canShowMobileCheckAvailabilityCta = isNewMobileSingleRoomFlow && !canViewOptionsForSticky
  const { isAnyVariant: shouldRemoveCheckAvailabilityCta } = useExperiment(
    ChertoExperiments.croRemoveCheckAvailability,
    { canActivate: isMobileDevice && availableForPurchase && canShowMobileCheckAvailabilityCta },
  )
  const shouldMountMobileAccommodationStickyFooter =
    canShowMobileCheckAvailabilityCta && !shouldRemoveCheckAvailabilityCta

  return <PendingLoginModalContext.Provider value={pendingLoginModalHandlers}>
    <ProductPaletteProvider product={offer}>
      <WishlistGuard>
      </WishlistGuard>
      <SocialProofDialog offer={offer} />

      <RelativeBlock onSubmit={(e) => e.preventDefault()}>
        {!isNewMobileHeaderUIEnabled && (
          <AccommodationOfferPageStickyHeaderMobile>
            <FlashMobileStickyHeaderFilters
              onCharterDurationChange={handleCharterDurationChange}
            />
          </AccommodationOfferPageStickyHeaderMobile>
        )}
        <CSSBreakpoint min="tablet" dynamic>
          <StyledFlashHotelStickyHeader
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
            className={clsx({ hideOnMobile: hideSoldOutOnMobile })}
          >
            {!headlessMode && <DesktopRelatedTravelItems offer={offer} />}
          </StyledFlashHotelStickyHeader>
        </CSSBreakpoint>
        {luxPlusEnabled && !!offer.luxPlus?.access &&
          <LuxPlusNotificationBanner>
            {offer.luxPlus.access === 'earlyAccess' && intl.formatMessage({
              id: 'A5yA5AlpSHEL24rB4ypR',
              defaultMessage: 'Members get early access to this offer before the general public and more',
              description: 'LuxPlus early access notification banner message on the offer detail page',
            })}
            {offer.luxPlus.access === 'memberOnly' && intl.formatMessage({
              id: 'mbgWcWDpuv2RDWPoUuYD',
              defaultMessage: 'You get exclusive access to this member-only offer',
              description: 'LuxPlus member-only notification banner message on the offer detail page',
            })}
          </LuxPlusNotificationBanner>}
        {availableForPurchase && <WalledContent offerType={offer.type} enforced={offer.walledGarden}>
          <Breakpoint only="mobile">
            <FlashHotelSidebarFilters
              capacities={occupancyLimits}
              optionD={packageSelectionExperiment.isOptionD}
            />
          </Breakpoint>
        </WalledContent>}
        {shouldShowLere && (
          <div>
            <ExpiredDealsLereRecommendations hideOffers={!!activeFilter} />
          </div>
        )}
        <div id={OFFER_GALLERY_TOP_ROOT_ID}>
          <OfferPageGallery
            offer={offer}
            shareModalConfig={shareModalConfig}
            autoPlayVideo={magicAutoPlayVideoOfferIds.has(offer.id) || !!offer.isVideoVimeoHero}
            charterBundleRoute={effectiveCharterBundleRoute}
            onTopHeroImageLoad={() => {
              if (topImageLoadSentRef.current) return
              topImageLoadSentRef.current = true
              trackDurationMs('flash_offer_top_image_load_time_ms', topImageStartMsRef.current)
            }}
          />
        </div>
        {isNewMobileHeaderUIEnabled && (
          <FilterContainer>
            <FlashMobileStickyHeaderFilters
              onCharterDurationChange={handleCharterDurationChange}
            />
          </FilterContainer>
        )}
        <Pane>
          <OfferIntro
            onViewPackageOptions={scrollToPackageOptions}
            flightDestination={destinationAirport}
            shareModalConfig={shareModalConfig}
          />
        </Pane>
        <ScrollAnchor ref={packageOptionsAnchor} />
        <SoftOfferPageWalledGarden offerType={offer?.type}>
          <PackageOptionsPane
            id="Package-Options"
            ref={packageSelectionRef}
            background={showPackageSelection || isRebrand ? 'light-background' : 'dark-background'}
            tabletBackground={isRebrand ? 'light-background' : 'normal-background'}
            className={clsx({ isCharterBundle: effectiveCharterBundleRoute })}
            data-flash-package-selection={showPackageSelection ? 'option_d' : undefined}
          >
            <PackageOptionsContent>
              <VerticalSpacer gap={32}>
                <CSSBreakpoint max="mobile" dynamic>
                  <FlashHotelMobileRoomOptions
                    offer={offer}
                    showFlightInformation={showFlightInformation}
                    optionD={packageSelectionExperiment.isOptionD}
                    charterFlightPreview={crossSellFlightPreview}
                  />
                </CSSBreakpoint>
                <CSSBreakpoint min="tablet" dynamic>
                  <FlashHotelDesktopRoomOptions
                    offer={offer}
                    optionD={packageSelectionExperiment.isOptionD}
                    setIsStickyFooterVisible={setIsStickyFooterVisible}
                    showFlightInformation={showFlightInformation}
                    charterDateRangesByMonth={dateRangesByMonth}
                    cheapestCharterDateRange={crossSellFlightPreview ?? cheapestDateRange}
                    onCharterDurationChange={handleCharterDurationChange}
                    occupancyLimits={occupancyLimits}
                    destinationAirport={destinationAirport}
                    desktopFilterBlockRef={desktopFilterBlockRef}
                  />
                </CSSBreakpoint>
              </VerticalSpacer>
            </PackageOptionsContent>
          </PackageOptionsPane>
          {isMobileDevice && availableForPurchase && shouldMountMobileAccommodationStickyFooter && !showPackageSelection && (
            <AccommodationOfferPageStickyFooter />
          )}
          {hasMounted && !isMobileDevice && availableForPurchase && !showPackageSelection && <HotelOfferPageStickyFooter
            visible={stickyFooterVisible}
            lowestPricePackage={offer.lowestPricePackage ?? offer.packages[0]}
            onScrollTo={scrollToPackageOptions}
            offer={offer}
          />}
        </SoftOfferPageWalledGarden>
      </RelativeBlock>
      <SoftOfferPageWalledGarden offerType={offer?.type} bypassWall={isWalledGardenEuropeOffersEnabled} wall={<FlashOfferSignUpWall offer={offer} packageView={bestPackage} saveUpToPercent={loggedOutSavedPercent} checkInDate={checkInDate} />} />
    </ProductPaletteProvider>
  </PendingLoginModalContext.Provider>
}

type MappedProps = Pick<Props,
  | 'airport'
  | 'airportSource'
  | 'duration'
  | 'activeFilter'
  | 'rooms'
  | 'checkInDate'
  | 'checkOutDate'
  | 'selectedCheckOutDate'
  | 'availableForPurchase'
  | 'bestPackage'
  | 'room'
  | 'activeHeaderFilter'
  | 'flightsEnabled'
  | 'isFlightsSelected'
  | 'roomMode'
  | 'sameRoomSelection'
  | 'flashPackageSelectionActive'
  | 'multipleRoomBookingActive'
  | 'canUseMultiRoomUI'
  | 'charterBundleRoute'
  | 'charterBundleEnabled'
  | 'fareClass'
  | 'priceMode'
  | 'isPriceModeChangedFromDefault'
  | 'effectiveBundledWithFlightsOnly'
  | 'effectiveCharterBundleRoute'
  | 'calendarDurationOptions'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  airport: state.airport,
  airportSource: state.airportSource,
  duration: state.duration,
  activeFilter: state.activeFilter,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
  selectedCheckOutDate: state.checkOutDate,
  room: state.room,
  flightsEnabled: offerFlightsEnabled(state, appState),
  isFlightsSelected: state.flights,
  availableForPurchase: isAvailableForPurchase(state, appState),
  bestPackage: getCheapestSelectedPackageOptionWithDefault(appState, state),
  activeHeaderFilter: state.activeHeaderFilter,
  roomMode: state.multiRoomMode,
  sameRoomSelection: state.flashSameRoomSelection,
  flashPackageSelectionActive: state.flashPackageSelectionActive,
  multipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
  canUseMultiRoomUI: getCanUseMultiRoomUI(state, appState),
  charterBundleRoute: state.charterBundleRoute,
  charterBundleEnabled: state.charterBundleEnabled,
  fareClass: state.fareClass,
  priceMode: state.priceMode,
  isPriceModeChangedFromDefault: state.isPriceModeChangedFromDefault,
  effectiveBundledWithFlightsOnly: getEffectiveBundledWithFlightsOnly(state),
  effectiveCharterBundleRoute: getEffectiveCharterBundleRoute(state),
  calendarDurationOptions: getCalendarDurationOptionsWithCharterBundleRestrictions(state, appState),
}), FlashHotelContent)
